import React, { useEffect, useState, useLayoutEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  Accordion,
  AccordionItem,
  Button,
  Image,
  Avatar,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Select,
  SelectItem,
} from "@nextui-org/react";
import { useTranslation } from "react-i18next";
import { BsPlus } from "react-icons/bs";
import { IoLogOut } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";

import Modal from "./Modal";
import MAudioPlayer from "./MAudioPlayer";
import AudioUploadForm from "./forms/AudioUploadForm";
import SuccessPlaceholder from "./SuccessPlaceholder";
import easierlabs_logo from "../assets/images/easierlabs_logo.webp";
import dashRoutes from "../routes/dash";

import { signout } from "../redux/actions";
import myAxios from "../utils/axiosWrapper";
import getConfig from "../conf/config";
import { auth } from "../conf/firebase";
import { AudioLogo } from "../assets/icons/audio-logo";
import { LogoutIcon } from "../assets/icons/audio-icons";
import { usePermissions, useWindowSize } from "../hooks";
import { getAlgoliaInfos } from "../redux/actions/algoliaActions";
import { useLanguage } from "../contexts/LanguageContext";

// import { auth, logout } from "../Auth/firebase";

const NavLinkItem = ({ route, index, displaySelectedState }) => {
  const { t } = useTranslation();
  return (
    <NavLink
      key={index}
      to={route.path}
      className={({ isActive }) => `text-[#404040] text-[16px] font-inter`}
    >
      {({ isActive }) => (
        <div
          className={`flex items-center gap-[12px] group px-[16px] py-[8px] rounded-md duration-500 ${
            displaySelectedState
              ? isActive
                ? "bg-[#a3a3a3]/[0.08]"
                : "hover:bg-[#a3a3a3]/[0.08]"
              : ""
          }`}
        >
          {route?.icon && (
            <div className="w-5 h-auto">
              <route.icon
                size={20}
                // className={`${
                //   isActive ? "text-primary" : "text-gray-400"
                // } group-hover:text-primary  duration-400`}
              />
            </div>
          )}
          <span className="w-full whitespace-nowrap">{t(route.name)}</span>
        </div>
      )}
    </NavLink>
  );
};

const CompanySwitcher = ({ currentCompanyId }) => {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState([]);
  const [isSwitching, setIsSwitching] = useState(false);

  useEffect(() => {
    // Wait for Firebase auth to be ready: on a page refresh auth.currentUser is
    // null for a moment and the axios interceptor would send the request
    // without an Authorization header, causing a 401.
    const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
      if (!firebaseUser) return;
      unsubscribe();
      myAxios
        .get(`${getConfig().API_URL}/audio/api/v1/admin/companies`)
        .then((res) => setCompanies(res?.data?.[0]?.companies || []))
        .catch((error) => console.error("Error fetching companies:", error));
    });
    return unsubscribe;
  }, []);

  const handleOnSwitch = async (companyId) => {
    if (!companyId || companyId === currentCompanyId) return;
    setIsSwitching(true);
    try {
      await myAxios.post(`${getConfig().API_URL}/audio/api/v1/admin/switch_company`, {
        company_id: companyId,
      });
      // Training pages hold ids (program, scenario, agent) that belong to the previous company
      // and would 404 in the new one: land on the program list instead.
      if (window.location.pathname.startsWith("/training")) {
        window.location.assign("/training/programs");
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error("Error switching company:", error);
      setIsSwitching(false);
    }
  };

  if (companies.length === 0) return null;

  return (
    <div className="px-3 py-2">
      <p className="text-xs text-gray-500 mb-1 px-1">{t("Company")}</p>
      <Select
        aria-label={t("Company")}
        size="sm"
        selectedKeys={currentCompanyId ? [currentCompanyId] : []}
        isDisabled={isSwitching}
        isLoading={isSwitching}
        onChange={(e) => handleOnSwitch(e.target.value)}
        classNames={{
          trigger: "items-center",
          innerWrapper: "pt-0",
        }}
      >
        {companies.map((company) => (
          <SelectItem
            key={company.id}
            value={company.id}
          >
            {company.name || company.id}
          </SelectItem>
        ))}
      </Select>
    </div>
  );
};

const Sidebar = ({ setIsAuth, menu, setMenu, setUpload }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);

  const { language, changeLanguage, setLanguage } = useLanguage();

  const languages = [
    { code: "fr", label: "Français", flag: "🇫🇷" },
    { code: "en", label: "English", flag: "🇬🇧" },
  ];
  const currentLang = language?.startsWith("fr") ? "fr" : "en";

  const { pathname } = useLocation();
  const { hasRequiredPermissions } = usePermissions();
  const [openUploadForm, setOpenUploadForm] = useState(false);
  const [openUploadConfirm, setOpenUploadConfirm] = useState(false);

  const [width, height] = useWindowSize();

  // const [user] = useAuthState(auth);

  const {
    scopes,
    company_config,
    loading: algoliaLoading,
  } = useSelector((state) => state.algolia.algolia);

  useEffect(() => {
    if (!scopes) {
      dispatch(getAlgoliaInfos());
    }
  }, [dispatch]);

  const isEasierAdmin = user?.is_easier_admin;

  const teamsScope = scopes?.teams || [];
  const departmentsScope = scopes?.departments || [];
  const localisationsScope = scopes?.localisations || [];
  // const hasGlobalVisibility = teamsScope.includes("all") || departmentsScope.includes("all") || localisationsScope.includes("all");
  const hasSimpleVisibility =
    teamsScope?.length === 0 && departmentsScope?.length === 0 && localisationsScope?.length === 0;

  const handleOnSignout = () => {
    dispatch(signout());
  };

  const isLarge = width > 1024;

  return (
    <>
      <div className="w-full max-w-[218px] lg:max-w-[290px] h-screen">
        <div className="fixed w-full max-w-[218px] lg:max-w-[290px] bg-white h-screen overflow-y-auto flex flex-col pb-2 lg:pb-4">
          <Link
            to="/dashboard"
            className="w-full flex items-center justify-center border-b border-b-gray-100 pt-[48px] pb-[34px]"
          >
            {/* <Image alt="NextUI hero Image" src={easierlabs_logo} /> */}
            <AudioLogo
              // width={isLarge ? 170 : 155}
              height={isLarge ? 54 : 48}
              // width={155}
              // height={48}
              // orignial ratio = 155 - 48
              // s = 218 - 68 (218 * 48 / 155)
              // l = 258 - 80
            />
          </Link>
          <div className="w-full flex flex-col gap-[8px] mt-[40px] flex-grow px-[16px]">
            {dashRoutes.map((route, index) => {
              const pageFeatureFlag = route?.featureFlag || false;
              const isFeatureFlagConditionValid = pageFeatureFlag
                ? company_config?.feature_flags?.[pageFeatureFlag] || false
                : true;
              const isGatedEasierAdminRoute = route?.easierAdminOnly;

              const canSeePage =
                hasRequiredPermissions(user, route.permissions, route?.defendedPermissions || []) &&
                !(route.managerOnly && hasSimpleVisibility) &&
                isFeatureFlagConditionValid &&
                !(isGatedEasierAdminRoute && !isEasierAdmin);

              if (!canSeePage) return;

              const hasVisibleChildren =
                route.children &&
                route.children.some((childRoute) => {
                  return (
                    hasRequiredPermissions(
                      user,
                      childRoute.permissions,
                      route?.defendedPermissions || []
                    ) && !(childRoute.managerOnly && hasSimpleVisibility)
                  );
                });

              return hasVisibleChildren ? (
                <Accordion
                  key={route.name}
                  isCompact
                  className="p-0"
                  selectedKeys={pathname.includes(route.path) ? ["0"] : []}
                >
                  {route.children.map((childRoute, i) => {
                    const canSeePage =
                      hasRequiredPermissions(
                        user,
                        childRoute.permissions,
                        route?.defendedPermissions || []
                      ) && !(childRoute.managerOnly && hasSimpleVisibility);

                    if (!canSeePage) return;

                    return (
                      <AccordionItem
                        key={i}
                        aria-label={route.name}
                        title={
                          <NavLinkItem
                            route={route}
                            index={index}
                          />
                        }
                        classNames={{
                          trigger:
                            "rounded-md p-0 hover:bg-[#a3a3a3]/[0.08] data-[open=true]:bg-[#a3a3a3]/[0.08]",
                          indicator:
                            "mr-4 -rotate-90 data-[open=true]:rotate-90 transition-transform duration-300 ease-in-out",
                          content: "p-3 pl-9",
                        }}
                        className="w-full duration-500"
                      >
                        <NavLinkItem
                          route={childRoute}
                          index={i}
                          displaySelectedState
                        />
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              ) : (
                <NavLinkItem
                  key={route.name}
                  route={route}
                  index={index}
                  displaySelectedState
                />
              );
            })}
          </div>
          <div className="px-4">
            <MAudioPlayer />
          </div>
          {isEasierAdmin && <CompanySwitcher currentCompanyId={user?.company_id} />}
          <div className="px-3 pt-3 border-t border-gray-100">
            <Popover
              placement="top"
              showArrow
              offset={10}
            >
              <PopoverTrigger>
                <div className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors">
                  <Avatar
                    name={`${user?.first_name?.charAt(0) || ""}${user?.last_name?.charAt(0) || ""}`}
                    className="w-9 h-9 flex-shrink-0"
                    classNames={{
                      base: "bg-primary/10 text-primary font-semibold text-sm",
                    }}
                  />
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="font-medium text-sm text-gray-900 truncate">
                      {`${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "Easier Labs"}
                    </span>
                    {user?.email && (
                      <span className="text-xs text-gray-400 truncate">{user.email}</span>
                    )}
                  </div>
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-0 rounded-xl shadow-lg border border-gray-100">
                <div className="p-4 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <Avatar
                      name={`${user?.first_name?.charAt(0) || ""}${
                        user?.last_name?.charAt(0) || ""
                      }`}
                      className="w-10 h-10"
                      classNames={{
                        base: "bg-primary/10 text-primary font-semibold text-sm",
                      }}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {`${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
                          "Easier Labs"}
                      </p>
                      {user?.email && (
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Language selector */}
                <div className="p-3 border-b border-gray-100">
                  <p className="text-xs text-gray-500 mb-2">{t("Language")}</p>
                  <div className="flex gap-2">
                    {languages.map((lang) => (
                      <div
                        key={lang.code}
                        onClick={() => {
                          changeLanguage(lang.code);
                          setLanguage(lang.code);
                        }}
                        onKeyDown={() => {
                          changeLanguage(lang.code);
                          setLanguage(lang.code);
                        }}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                          currentLang === lang.code
                            ? "bg-primary/10 text-primary"
                            : "hover:bg-gray-100 text-gray-600"
                        }`}
                      >
                        <span className="text-base">{lang.flag}</span>
                        <span className="text-sm">{lang.code.toUpperCase()}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-2">
                  <div
                    onClick={handleOnSignout}
                    onKeyDown={handleOnSignout}
                    className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                  >
                    <LogoutIcon className="w-4 h-4" />
                    <span>{t("Sign out")}</span>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
      <Modal
        isOpen={openUploadForm}
        handleOnClose={() => setOpenUploadForm(false)}
        handleOnOpenChange={(isOpen) => setOpenUploadForm(isOpen)}
        modalHeaderComponent={<AudioUploadModalHeader />}
        modalBodyComponent={
          <AudioUploadForm
            onSubmitForm={() => {
              setOpenUploadForm(false);
              setOpenUploadConfirm(true);
            }}
          />
        }
        showFooter={false}
        size="2xl"
      />
      <Modal
        isOpen={openUploadConfirm}
        modalHeaderComponent={null}
        modalBodyComponent={<SuccessPlaceholder onPress={() => setOpenUploadConfirm(false)} />}
        handleOnClose={() => setOpenUploadConfirm(false)}
        handleOnOpenChange={(isOpen) => setOpenUploadConfirm(isOpen)}
        showFooter={false}
        size="xl"
      />
    </>
  );
};

export default Sidebar;

const AudioUploadModalHeader = () => {
  const { t } = useTranslation();
  return <h1 className="w-full text-xl font-semibold text-center py-4">{t("Upload")}</h1>;
};
